import {Box, Button, Chip, Divider, Stack, Typography} from '@mui/material';
import {alpha} from '@mui/material/styles';
import {format, isValid, parseISO} from 'date-fns';
import {
	ReferralStatusEnum,
	ReferralTypeEnum,
} from 'domain/usecases/private/my-referrals-flow/get-referrals';
import {MyReferralsTaggingEnum} from 'main/analytics/gaTagsEnum';
import {AlertInfo, CopySection, StyledCard} from 'presentation/components';
import {PhonesDrawer} from 'presentation/components/PhonesDrawer';
import {PhonesMenu} from 'presentation/components/PhonesMenu';
import {AuthorizationsEnum, CopySectionMaskEnum, MyAppointmentsEnum} from 'presentation/constants';
import Icon from 'presentation/icons';
import {translations} from 'presentation/translations-new';
import {textHandle} from 'presentation/utils/textHandle';
import React, {Fragment} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {useReferralCard} from './Hooks/useReferralCard';
import {AuthorizationCardContainer} from './styles';
import {ReferralsCardProps} from './types';

const {showDetails, call} = translations['pt-br'].commons;
const {
	goToAuthorization,
	surgeryAlert,
	scheduleAppointment,
	scheduleExam,
	seeAppointments,
	passwordLabel,
	validityLabel,
	passwordCopiedMessage,
} = translations['pt-br'].privateFlow.myReferrals;

const {choosePhoneNumber} = translations['pt-br'].privateFlow.bookingFlow.unitsFlow.unitDetails;

export function ReferralCard({data, handleOpenDetails, handleOpenSchedule}: ReferralsCardProps) {
	const {
		contentList,
		borderColor,
		chipText,
		chipVariant,
		chipTextColor,
		theme,
		shouldHideDetailsButton,
		anchorElState,
		handleClosePhoneMenu,
		handleOpenPhoneMenu,
		openPhoneDrawerState,
		phoneNumbers,
		handleTogglePhoneDrawer,
		isPhoneNumberMenuOpen,
		isMobile,
	} = useReferralCard(data);
	const navigate = useNavigate();
	const {pathname} = useLocation();
	const isSuccess = data.status === ReferralStatusEnum.SUCCESS;
	const isScheduled = data.status === ReferralStatusEnum.SCHEDULED;
	const showPassword = (isSuccess || isScheduled) && Boolean(data.cdSenha);
	const parsedValidityDate = data.dtValidade ? parseISO(data.dtValidade) : undefined;
	const formattedValidityDate =
		parsedValidityDate && isValid(parsedValidityDate)
			? format(parsedValidityDate, 'dd/MM/yyyy')
			: undefined;
	const showAuthorizationData = showPassword || Boolean(formattedValidityDate);

	const getActionButton = () => {
		const isFinished = data.status === ReferralStatusEnum.FINISHED;
		const isSurgery = data.tipoProcedimento === ReferralTypeEnum.SURGERY;
		const isPending = data.status === ReferralStatusEnum.PENDING;
		const isExam = data.tipoProcedimento === ReferralTypeEnum.EXAM;

		if (isSurgery && isSuccess) {
			return (
				<Fragment>
					<AlertInfo.Root>
						<AlertInfo.Description>{surgeryAlert}</AlertInfo.Description>
					</AlertInfo.Root>
					<Button
						data-tag-id={
							MyReferralsTaggingEnum.CLICK_LIGAR_CIRURGIA_MEUS_ENCAMINHAMENTOS
						}
						onClick={e => handleOpenPhoneMenu(e)}>
						{call}
					</Button>
				</Fragment>
			);
		}

		if (isSuccess) {
			return (
				<Button
					data-tag-id={MyReferralsTaggingEnum.CLICK_AGENDAR_MEUS_ENCAMINHAMENTOS}
					onClick={handleOpenSchedule}>
					{isExam ? scheduleExam : scheduleAppointment}
				</Button>
			);
		}

		if (isScheduled) {
			return (
				<Button
					data-tag-id={MyReferralsTaggingEnum.CLICK_VER_AGENDAMENTO_MEUS_ENCAMINHAMENTOS}
					onClick={() => navigate(MyAppointmentsEnum.MY_APPOINTMENTS, {state: pathname})}>
					{seeAppointments}
				</Button>
			);
		}

		if (!isSuccess && !isFinished && !isScheduled) {
			return (
				<Button
					onClick={() =>
						navigate(
							isPending
								? AuthorizationsEnum.REQUEST
								: AuthorizationsEnum.AUTHORIZATIONS,
							{state: pathname},
						)
					}
					data-tag-id={
						isPending
							? MyReferralsTaggingEnum.CLICK_IR_PARA_AUTORIZACAO_EM_ANDAMENTO_MEUS_ENCAMINHAMENTOS
							: MyReferralsTaggingEnum.CLICK_IR_PARA_AUTORIZACAO_HISTORICO_MEUS_ENCAMINHAMENTOS
					}>
					{goToAuthorization}
				</Button>
			);
		}

		return null;
	};

	return (
		<AuthorizationCardContainer $borderColor={borderColor}>
			<Stack gap={1}>
				<Typography
					variant="h6"
					fontWeight={400}
					fontSize="1.125rem"
					lineHeight="143%"
					color="primary">
					{textHandle(data.nomeProcedimento).capitalizeFirstLetter() ?? ''}
				</Typography>

				<Stack
					direction="row"
					py="6px"
					alignItems="center"
					height="48.5px"
					justifyContent={'space-between'}>
					<Chip
						size="small"
						label={chipText}
						color={chipVariant}
						sx={{
							color: chipTextColor ?? 'text.primary',
							backgroundColor:
								chipVariant === 'error'
									? alpha(theme.palette.error.main, 0.3)
									: undefined,
						}}
					/>
					{shouldHideDetailsButton ? null : (
						<Button
							variant="text"
							onClick={() => handleOpenDetails(data)}
							endIcon={
								<Icon
									name="chevron-right-outlined"
									color={theme.palette.primary.main}
									size={20}
								/>
							}
							data-tag-id={
								MyReferralsTaggingEnum.CLICK_VER_DETALHES_MEUS_ENCAMINHAMENTOS
							}>
							{showDetails}
						</Button>
					)}
				</Stack>
			</Stack>

			<Divider />
			<StyledCard.Content sx={{flexDirection: 'column'}} list={contentList} />
			{showAuthorizationData ? (
				<Stack gap={1}>
					<Stack
						direction="row"
						alignItems="center"
						justifyContent="space-between"
						gap={1}>
						{showPassword ? (
							<Stack direction="row" alignItems="center" gap={1}>
								<Icon
									name="password-info-filled"
									color={theme.palette.primary.main}
									size={20}
								/>
								<Typography variant="body2">{passwordLabel}</Typography>
							</Stack>
						) : null}
						{formattedValidityDate ? (
							<Typography
								variant="helperText"
								fontWeight={600}
								color="#CC6302"
								ml="auto">
								{validityLabel} {formattedValidityDate}
							</Typography>
						) : null}
					</Stack>
					{showPassword ? (
						<CopySection
							dataTagId={{
								copy: MyReferralsTaggingEnum.CLICK_COPIAR_SENHA_LIBERADO_MEUS_ENCAMINHAMENTOS,
							}}
							value={data.cdSenha}
							mask={CopySectionMaskEnum.WITHOUT_MASK}
							highlightedIcons
							snackbarMessage={passwordCopiedMessage}
							customPadding="8px 16px"
						/>
					) : null}
				</Stack>
			) : null}
			{getActionButton()}
			{isMobile ? (
				<PhonesDrawer.Root
					open={openPhoneDrawerState}
					toggleDrawer={handleTogglePhoneDrawer}
					title={choosePhoneNumber}>
					<AlertInfo.Root>{phoneNumbers.alert}</AlertInfo.Root>
					<PhonesDrawer.WithTitle
						dataTagId={{
							copy: MyReferralsTaggingEnum.CLICK_COPIAR_TELEFONE_CIRURGIA_MEUS_ENCAMINHAMENTOS,
							call: MyReferralsTaggingEnum.CLICK_ICONE_LIGAR_CIRURGIA_MEUS_ENCAMINHAMENTOS,
						}}
						phoneNumbers={phoneNumbers.phones}
					/>
				</PhonesDrawer.Root>
			) : (
				<PhonesMenu.Root
					open={isPhoneNumberMenuOpen}
					onClose={handleClosePhoneMenu}
					anchorEl={anchorElState}
					anchorOrigin={{
						vertical: 'bottom',
						horizontal: 'center',
					}}
					transformOrigin={{
						vertical: 'top',
						horizontal: 'center',
					}}>
					<Box pb={2}>
						<AlertInfo.Root>{phoneNumbers.alert}</AlertInfo.Root>
					</Box>
					<PhonesDrawer.WithTitle
						dataTagId={{
							copy: MyReferralsTaggingEnum.CLICK_COPIAR_TELEFONE_CIRURGIA_MEUS_ENCAMINHAMENTOS,
							call: MyReferralsTaggingEnum.CLICK_ICONE_LIGAR_CIRURGIA_MEUS_ENCAMINHAMENTOS,
						}}
						phoneNumbers={phoneNumbers.phones}
					/>
				</PhonesMenu.Root>
			)}
		</AuthorizationCardContainer>
	);
}
